import axios from 'axios';
import { API_BASE_URL, getApiUrl } from '../config/api.config';
import { navigateToLogin } from './navigation';

// Re-export for backward compatibility
export { API_BASE_URL, getApiUrl };

const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		'Content-Type': 'application/json'
	},
	timeout: 30000 // 30 seconds timeout
});

// Add token to requests (single-tenant model - no tenant header needed)
api.interceptors.request.use((config) => {
	const token = localStorage.getItem('token');
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}

	// Note: X-Tenant-ID header removed - using single-tenant model
	// console.log('DEBUG: Making API Request:', config.method.toUpperCase(), config.url);
	return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error.response) {
			// Server responded with error status
			const { status, data } = error.response;

			switch (status) {
				case 401:
					// If this is a login attempt, don't trigger global logout
					// Let the login component handle the invalid credentials error
					if (error.config && error.config.url && error.config.url.includes('/auth/login')) {
						throw error;
					}

					// Unauthorized - redirect to login (SPA navigation)
					localStorage.removeItem('token');
					localStorage.removeItem('user');
					localStorage.removeItem('loginTime');
					window.dispatchEvent(new CustomEvent('auth:logout'));
					navigateToLogin();
					break;
				case 403:
					// Forbidden - could be JWT signature error or access denied
					// If the error message suggests JWT issues, clear the token
					const errorMessage = data.message || error.message || '';
					if (errorMessage.toLowerCase().includes('jwt') ||
						errorMessage.toLowerCase().includes('signature') ||
						errorMessage.toLowerCase().includes('token')) {
						console.warn('JWT authentication error detected - clearing token');
						localStorage.removeItem('token');
						localStorage.removeItem('user');
						localStorage.removeItem('loginTime');
						window.dispatchEvent(new CustomEvent('auth:logout'));
						navigateToLogin();
					} else {
						throw new Error('Access denied: You don\'t have permission to perform this action');
					}
					break;
				case 404:
					throw new Error('Resource not found');
				case 413:
					throw new Error('File too large. Maximum size is 100MB');
				case 422:
					// Validation error
					if (data.validationErrors) {
						const errorMessages = Object.values(data.validationErrors).join(', ');
						throw new Error(`Validation error: ${errorMessages}`);
					}
					throw new Error(data.message || 'Invalid data provided');
				default:
					throw new Error(data.message || data.error || `Server error: ${status}`);
			}
		} else if (error.request) {
			// Network error
			throw new Error('Network error: Unable to connect to the server');
		} else {
			// Other error
			throw new Error(error.message || 'An unexpected error occurred');
		}
	}
);

// Auth API
export const authAPI = {
	login: async (email, password, tenantIdentifier) => {
		const response = await api.post('/auth/login', { email, password, tenantIdentifier });
		return response.data;
	},
	forgotPassword: async (email) => {
		const response = await api.post('/auth/forgot-password', { email });
		return response.data;
	},
	resetPassword: async (token, newPassword) => {
		const response = await api.post('/auth/reset-password', { token, newPassword });
		return response.data;
	}
};

// Current user account (all authenticated roles)
export const userAPI = {
	changeMyPassword: async (currentPassword, newPassword) => {
		const response = await api.put('/users/me/password', { currentPassword, newPassword });
		return response.data;
	}
};

// Admin API
export const adminAPI = {
	createUser: async (userData) => {
		const response = await api.post('/admin/users/create', userData);
		return response.data;
	},

	getAllFilesForAdmin: async () => {
		const response = await api.get('/admin/files');
		return response.data;
	},
	getAllUsers: async () => {
		const response = await api.get('/admin/users');
		return response.data;
	},
	getStartups: async () => {
		const response = await api.get('/admin/users/startups');
		return response.data;
	},
	getInvestors: async () => {
		const response = await api.get('/admin/users/investors');
		return response.data;
	},
	createStartupProfile: async (profileData) => {
		const response = await api.post('/admin/startup/profile', profileData);
		return response.data;
	},
	getStartupProfile: async (id) => {
		const response = await api.get(`/admin/startup/${id}`);
		return response.data;
	},
	getAllFiles: async () => {
		const response = await api.get('/admin/files');
		return response.data;
	},
	updateFilePermissions: async (fileId, investorIds) => {
		const response = await api.post(`/admin/files/${fileId}/permissions`, {
			investorIds
		});
		return response.data;
	},
	getFilePermissions: async (fileId) => {
		const response = await api.get(`/admin/files/${fileId}/permissions`);
		return response.data;
	},
	getStartupDirectors: async (startupId) => {
		const response = await api.get(`/admin/startups/${startupId}/directors`);
		return response.data;
	},
	getDirectorsByUserId: async (userId) => {
		const response = await api.get(`/admin/users/${userId}/directors`);
		return response.data;
	},
	createDirector: async (startupId, directorData) => {
		const response = await api.post(`/admin/startups/${startupId}/directors`, directorData);
		return response.data;
	},
	createDirectorByUserId: async (userId, directorData) => {
		const response = await api.post(`/admin/users/${userId}/directors`, directorData);
		return response.data;
	},
	updateDirector: async (directorId, directorData) => {
		const response = await api.put(`/admin/directors/${directorId}`, directorData);
		return response.data;
	},
	deleteDirector: async (directorId) => {
		const response = await api.delete(`/admin/directors/${directorId}`);
		return response.data;
	},
	getEventOrganizers: async () => {
		const response = await api.get('/admin/users/event-organizers');
		return response.data;
	},
	/** Generate a temporary password for a user (platform admin or scoped investor-network admin). */
	resetUserPassword: async (userId) => {
		const response = await api.post(`/admin/users/${userId}/reset-password`);
		return response.data;
	},
	/** Set platform review status for a startup (ADMIN / TENANT_ADMIN). */
	updateStartupValidation: async (startupId, data) => {
		const response = await api.put(`/admin/startups/${startupId}/validation`, data);
		return response.data;
	}
};

// Startup API
export const startupAPI = {
	uploadFile: async (file) => {
		const formData = new FormData();
		formData.append('file', file);
		const response = await api.post('/files/upload', formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});
		return response.data;
	},
	getMyFiles: async () => {
		const response = await api.get('/files/mine');
		return response.data;
	},
	updateProfile: async (profileData) => {
		const response = await api.post('/startup/profile/update', profileData);
		return response.data;
	},
	getMyProfile: async () => {
		const response = await api.get('/startup/profile');
		return response.data;
	},
	getMyStartupProfile: async () => {
		const response = await api.get('/users/me/startup-profile');
		return response.data;
	},
	getMyStartupDirectors: async () => {
		const response = await api.get('/users/me/startup-directors');
		return response.data;
	},
	updateMyStartupProfile: async (profileData) => {
		const response = await api.put('/users/me/startup-profile', profileData);
		return response.data;
	},
	submitInvestmentApplication: async (payload) => {
		const response = await api.post('/startup-investment-applications', payload);
		return response.data;
	},
	getMyInvestmentApplications: async () => {
		const response = await api.get('/startup-investment-applications/mine');
		return response.data;
	},
	getApplicationTargets: async (targetType) => {
		const response = await api.get('/startup-investment-applications/targets', {
			params: { targetType }
		});
		return response.data;
	}
};

// Startup Management API (for admin views)
export const startupManagementAPI = {
	getById: async (id) => {
		const response = await api.get(`/admin/users/${id}`);
		return response.data;
	},
	update: async (id, data) => {
		const response = await api.put(`/admin/users/${id}`, data);
		return response.data;
	}
};

// Investor API
export const investorAPI = {
	getSharedFiles: async () => {
		const response = await api.get('/files/shared-with-me');
		return response.data;
	},
	getPortfolio: async (investorId) => {
		const response = await api.get(`/investments/investor/${investorId}/portfolio`);
		return response.data;
	},
	getInvestments: async (investorId) => {
		const response = await api.get(`/investments/investor/${investorId}`);
		return response.data;
	},
	getEvents: async (investorId) => {
		const response = await api.get(`/pitch-events/investor/${investorId}`);
		return response.data;
	},
	getStartupProfile: async (startupId) => {
		const response = await api.get(`/investments/startup/${startupId}/profile`);
		return response.data;
	},
	getStartupDirectors: async (startupId) => {
		const response = await api.get(`/investments/startup/${startupId}/directors`);
		return response.data;
	},
	getStartupInvestments: async (startupId) => {
		const response = await api.get(`/investments/startup/${startupId}`);
		return response.data;
	}
};

// File API
export const fileAPI = {
	getFilePreview: async (fileId) => {
		// Fetch the file as a blob with Authorization header
		const token = localStorage.getItem('token');
		const response = await fetch(`${API_BASE_URL}/files/${fileId}/preview`, {
			headers: {
				'Authorization': `Bearer ${token}`
			}
		});

		if (!response.ok) {
			const errorText = await response.text();
			let errorMessage = 'Failed to load file preview';
			try {
				const errorJson = JSON.parse(errorText);
				errorMessage = errorJson.message || errorMessage;
			} catch (e) {
				errorMessage = errorText || errorMessage;
			}
			throw new Error(errorMessage);
		}

		const blob = await response.blob();

		// Check if the blob is actually a file or an error response
		if (blob.size === 0) {
			throw new Error('File is empty or could not be loaded');
		}

		// Check if the response is actually a blob (not an error JSON)
		const contentType = response.headers.get('Content-Type') || '';
		if (contentType.includes('application/json')) {
			const text = await blob.text();
			try {
				const errorJson = JSON.parse(text);
				throw new Error(errorJson.message || 'Failed to load file preview');
			} catch (e) {
				// If it's not JSON, it might be the actual file
			}
		}

		const previewUrl = URL.createObjectURL(blob);

		return {
			previewUrl,
			contentType: response.headers.get('Content-Type')
		};
	},
	getFileDownload: async (fileId) => {
		const response = await api.get(`/files/${fileId}/download`, {
			responseType: 'blob'
		});
		return response;
	},
	getFileMetadata: async (fileId) => {
		const response = await api.get(`/files/${fileId}/metadata`);
		return response.data;
	},
	deleteFile: async (fileId) => {
		const response = await api.delete(`/files/${fileId}`);
		return response.data;
	},
	getMyFiles: async () => {
		const response = await api.get('/files/mine');
		return response.data;
	},
	updateFile: async (fileId, { description, fileCategory, documentType, visibility, tags }) => {
		const formData = new FormData();
		if (description !== undefined) formData.append('description', description);
		if (fileCategory !== undefined) formData.append('fileCategory', fileCategory);
		if (documentType !== undefined) formData.append('documentType', documentType);
		if (visibility !== undefined) formData.append('visibility', visibility);
		if (tags !== undefined) formData.append('tags', tags);

		const response = await api.put(`/files/${fileId}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});
		return response.data;
	},
	getFilesByModule: async (moduleType, moduleId) => {
		const response = await api.get('/files', {
			params: { moduleType, moduleId }
		});
		return response.data;
	},
	uploadFileWithProgress: async (file, onProgress) => {
		const formData = new FormData();
		formData.append('file', file);

		return new Promise((resolve, reject) => {
			api.post('/files/upload', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				onUploadProgress: (progressEvent) => {
					const percentCompleted = Math.round(
						(progressEvent.loaded * 100) / progressEvent.total
					);
					if (onProgress) {
						onProgress(percentCompleted);
					}
				}
			})
				.then((response) => resolve(response.data))
				.catch((error) => reject(error));
		});
	},
	uploadFileForInvestor: async (formData, onProgress) => {
		return new Promise((resolve, reject) => {
			api.post('/files/investor/upload', formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				onUploadProgress: (progressEvent) => {
					const percentCompleted = Math.round(
						(progressEvent.loaded * 100) / progressEvent.total
					);
					if (onProgress) {
						onProgress(percentCompleted);
					}
				}
			})
				.then((response) => resolve(response.data))
				.catch((error) => reject(error));
		});
	},
	// Share file with users, roles, or investors
	shareFile: async (shareData) => {
		const response = await api.post('/files/share', shareData);
		return response.data;
	},
	// Revoke file permission
	revokePermission: async (permissionId) => {
		const response = await api.delete(`/files/permissions/${permissionId}`);
		return response.data;
	},
	// Get file permissions
	getFilePermissions: async (fileId) => {
		const response = await api.get(`/files/${fileId}/permissions`);
		return response.data;
	},
	// Get investors related to current startup (for file sharing)
	getRelatedInvestors: async () => {
		const response = await api.get('/files/related-investors');
		return response.data;
	},
	// Get events related to current startup (for file sharing)
	getRelatedEvents: async () => {
		const response = await api.get('/files/related-events');
		return response.data;
	}
};

// Pitch Event API
export const pitchEventAPI = {
	// Get all pitch events
	getAllEvents: async () => {
		const response = await api.get('/pitch-events');
		return response.data;
	},

	// Get upcoming events
	getUpcomingEvents: async () => {
		const response = await api.get('/pitch-events/upcoming');
		return response.data;
	},

	// Get event by ID
	getEvent: async (id) => {
		const response = await api.get(`/pitch-events/${id}`);
		return response.data;
	},

	// Create new event
	createEvent: async (eventData) => {
		const response = await api.post('/pitch-events', eventData);
		return response.data;
	},

	// Update event
	updateEvent: async (id, eventData) => {
		const response = await api.put(`/pitch-events/${id}`, eventData);
		return response.data;
	},

	// Delete event
	deleteEvent: async (id) => {
		const response = await api.delete(`/pitch-events/${id}`);
		return response.data;
	},

	// Apply to event
	applyToEvent: async (id, applicationData) => {
		const response = await api.post(`/pitch-events/${id}/apply`, applicationData);
		return response.data;
	},

	// Update participant stage
	updateParticipantStage: async (eventId, participantId, stage) => {
		const response = await api.put(`/pitch-events/${eventId}/participants/${participantId}/stage`, { stage });
		return response.data;
	},

	// Update participant status
	updateParticipantStatus: async (eventId, participantId, statusData) => {
		const response = await api.put(`/pitch-events/${eventId}/participants/${participantId}/status`, statusData);
		return response.data;
	},

	// Get events by startup
	getByStartup: async (startupId) => {
		const response = await api.get(`/pitch-events/startup/${startupId}`);
		return response.data;
	},

	// Get event participants
	getEventParticipants: async (eventId) => {
		const response = await api.get(`/pitch-events/${eventId}/participants`);
		return response.data;
	},

	// Remove participant from event
	removeParticipant: async (eventId, participantId) => {
		const response = await api.delete(`/pitch-events/${eventId}/participants/${participantId}`);
		return response.data;
	},

	// Get event investors
	getEventInvestors: async (eventId) => {
		const response = await api.get(`/pitch-events/${eventId}/investors`);
		return response.data;
	},

	// Add investor to event
	addEventInvestor: async (eventId, userId) => {
		const response = await api.post(`/pitch-events/${eventId}/investors`, { userId });
		return response.data;
	},

	// Remove investor from event
	removeEventInvestor: async (eventId, investorId) => {
		const response = await api.delete(`/pitch-events/${eventId}/investors/${investorId}`);
		return response.data;
	}
};

// Health check API
export const healthAPI = {
	checkHealth: async () => {
		try {
			const response = await api.get('/health');
			return response.data;
		} catch (error) {
			// Try fallback endpoint
			try {
				const response = await fetch(`${API_BASE_URL}/actuator/health`);
				return await response.json();
			} catch (fallbackError) {
				throw error; // Throw original error if fallback fails
			}
		}
	}
};

// Subscription API
export const subscriptionAPI = {
	// Get all available plans
	getPlans: async () => {
		const response = await api.get('/subscriptions/plans');
		return response.data;
	},

	// Get current user's subscription
	getMySubscription: async () => {
		const response = await api.get('/subscriptions/my-subscription');
		return response.data;
	},

	// Subscribe to a plan
	subscribe: async (planCode) => {
		const response = await api.post('/subscriptions/subscribe', { planCode });
		return response.data;
	},

	// Cancel subscription
	cancelSubscription: async (immediate = false) => {
		const response = await api.post(`/subscriptions/cancel?immediate=${immediate}`);
		return response.data;
	},

	// Renew subscription
	renewSubscription: async () => {
		const response = await api.post('/subscriptions/renew');
		return response.data;
	},

	// Get available addons
	getAddons: async () => {
		const response = await api.get('/subscriptions/addons');
		return response.data;
	},

	// Get user's purchased addons
	getMyAddons: async () => {
		const response = await api.get('/subscriptions/my-addons');
		return response.data;
	},

	// Purchase addon
	purchaseAddon: async (addonCode, quantity = 1) => {
		const response = await api.post('/subscriptions/addons/purchase', { addonCode, quantity });
		return response.data;
	},

	// Get usage summary
	getUsage: async () => {
		const response = await api.get('/subscriptions/usage');
		return response.data;
	},

	// Investor activity fee (one-time ₹1000 to add documents or startups)
	getInvestorActivityStatus: async () => {
		const response = await api.get('/subscriptions/investor-activity-status');
		return response.data;
	},

	payInvestorActivityFee: async () => {
		const response = await api.post('/subscriptions/investor-activity-fee/pay');
		return response.data;
	}
};

// Billing API
export const billingAPI = {
	// Get user's invoices
	getInvoices: async () => {
		const response = await api.get('/billing/invoices');
		return response.data;
	},

	// Get pending invoices
	getPendingInvoices: async () => {
		const response = await api.get('/billing/invoices/pending');
		return response.data;
	},

	// Get invoice by ID
	getInvoice: async (invoiceId) => {
		const response = await api.get(`/billing/invoices/${invoiceId}`);
		return response.data;
	},

	// Download invoice PDF
	downloadInvoice: async (invoiceId) => {
		const token = localStorage.getItem('token');
		const response = await fetch(`${API_BASE_URL}/billing/invoices/${invoiceId}/download`, {
			headers: {
				'Authorization': `Bearer ${token}`
			}
		});

		if (!response.ok) {
			throw new Error('Failed to download invoice');
		}

		const blob = await response.blob();
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `invoice-${invoiceId}.pdf`;
		document.body.appendChild(a);
		a.click();
		window.URL.revokeObjectURL(url);
		document.body.removeChild(a);
	},

	// Get upcoming billing info
	getUpcomingBilling: async () => {
		const response = await api.get('/billing/upcoming');
		return response.data;
	}
};

// Payment API
export const paymentAPI = {
	// Create Razorpay order
	createOrder: async (invoiceId) => {
		const response = await api.post('/payments/create-order', { invoiceId });
		return response.data;
	},

	// Verify payment
	verifyPayment: async (paymentId, orderId, signature) => {
		const response = await api.post('/payments/verify', { paymentId, orderId, signature });
		return response.data;
	},

	// Get payment history
	getPaymentHistory: async () => {
		const response = await api.get('/payments/history');
		return response.data;
	},

	// Get payment by ID
	getPayment: async (paymentId) => {
		const response = await api.get(`/payments/${paymentId}`);
		return response.data;
	},

	// Process refund
	processRefund: async (paymentId, amount) => {
		const response = await api.post(`/payments/${paymentId}/refund`, { amount });
		return response.data;
	}
};

// Usage API
export const usageAPI = {
	// Get storage usage
	getStorageUsage: async () => {
		const response = await api.get('/usage/storage');
		return response.data;
	},

	// Get investor link usage
	getInvestorLinkUsage: async () => {
		const response = await api.get('/usage/investor-links');
		return response.data;
	},

	// Get usage summary
	getUsageSummary: async () => {
		const response = await api.get('/usage/summary');
		return response.data;
	},

	// Get usage alerts
	getUsageAlerts: async () => {
		const response = await api.get('/usage/alerts');
		return response.data;
	}
};

// Investment Round API
export const investmentRoundAPI = {
	// Investment Tranches (API still uses /investment-rounds endpoints)
	createRound: async (data) => {
		const response = await api.post('/investment-rounds', data);
		return response.data;
	},

	getAllRounds: async () => {
		const response = await api.get('/investment-rounds');
		return response.data;
	},

	getRound: async (id) => {
		const response = await api.get(`/investment-rounds/${id}`);
		return response.data;
	},

	getRoundsByStartup: async (startupId) => {
		const response = await api.get(`/investment-rounds/startup/${startupId}`);
		return response.data;
	},

	updateRound: async (id, data) => {
		const response = await api.put(`/investment-rounds/${id}`, data);
		return response.data;
	},

	closeRound: async (id) => {
		const response = await api.put(`/investment-rounds/${id}/close`);
		return response.data;
	},

	deleteRound: async (id) => {
		const response = await api.delete(`/investment-rounds/${id}`);
		return response.data;
	},

	// Network Commitments
	createCommitment: async (roundId, data) => {
		const response = await api.post(`/investment-rounds/${roundId}/commitments`, data);
		return response.data;
	},

	getCommitmentsByRound: async (roundId) => {
		const response = await api.get(`/investment-rounds/${roundId}/commitments`);
		return response.data;
	},

	getCommitment: async (id) => {
		const response = await api.get(`/commitments/${id}`);
		return response.data;
	},

	getCommitmentsByNetwork: async (networkId) => {
		const response = await api.get(`/commitments/network/${networkId}`);
		return response.data;
	},

	updateCommitment: async (id, data) => {
		const response = await api.put(`/investment-rounds/commitments/${id}`, data);
		return response.data;
	},

	deleteCommitment: async (id) => {
		const response = await api.delete(`/commitments/${id}`);
		return response.data;
	},

	// Investor Participations
	createParticipation: async (commitmentId, data) => {
		const response = await api.post(`/commitments/${commitmentId}/participations`, data);
		return response.data;
	},

	getParticipationsByCommitment: async (commitmentId) => {
		const response = await api.get(`/commitments/${commitmentId}/participations`);
		return response.data;
	},

	getParticipationsByInvestor: async (investorId) => {
		const response = await api.get(`/participations/investor/${investorId}`);
		return response.data;
	},

	updateParticipation: async (id, data) => {
		const response = await api.put(`/participations/${id}`, data);
		return response.data;
	},

	deleteParticipation: async (id) => {
		const response = await api.delete(`/participations/${id}`);
		return response.data;
	},

	// Investment Terms
	createTerm: async (commitmentId, data) => {
		const response = await api.post(`/commitments/${commitmentId}/terms`, data);
		return response.data;
	},

	getTermsByCommitment: async (commitmentId) => {
		const response = await api.get(`/commitments/${commitmentId}/terms`);
		return response.data;
	},

	getTerm: async (id) => {
		const response = await api.get(`/terms/${id}`);
		return response.data;
	},

	updateTerm: async (id, data) => {
		const response = await api.put(`/terms/${id}`, data);
		return response.data;
	},

	markTermAsPaid: async (id, transactionReference) => {
		const response = await api.put(`/terms/${id}/pay`, { transactionReference });
		return response.data;
	},

	getPendingTerms: async () => {
		const response = await api.get('/terms/pending');
		return response.data;
	},

	getOverdueTerms: async () => {
		const response = await api.get('/terms/overdue');
		return response.data;
	},

	getUpcomingTerms: async (days = 30) => {
		const response = await api.get(`/terms/upcoming?days=${days}`);
		return response.data;
	},

	deleteTerm: async (id) => {
		const response = await api.delete(`/terms/${id}`);
		return response.data;
	},

	// Calculations
	getNetworkPortfolio: async (networkId) => {
		const response = await api.get(`/calculations/network/${networkId}/portfolio`);
		return response.data;
	},

	getInvestorPortfolio: async (investorId) => {
		const response = await api.get(`/calculations/investor/${investorId}/portfolio`);
		return response.data;
	},

	getStartupCapTable: async (startupId) => {
		const response = await api.get(`/calculations/startup/${startupId}/captable`);
		return response.data;
	},

	getCommitmentSummary: async (commitmentId) => {
		const response = await api.get(`/calculations/commitment/${commitmentId}/summary`);
		return response.data;
	},

	getRoundSummary: async (roundId) => {
		const response = await api.get(`/calculations/round/${roundId}/summary`);
		return response.data;
	},

	getDisbursementProgress: async (commitmentId) => {
		const response = await api.get(`/calculations/commitment/${commitmentId}/disbursement-progress`);
		return response.data;
	},

	calculateDilution: async (startupId, newInvestment, preMoneyValuation) => {
		const response = await api.post(`/calculations/startup/${startupId}/dilution`, {
			newInvestment,
			preMoneyValuation
		});
		return response.data;
	},

	calculateEquity: async (investmentAmount, postMoneyValuation) => {
		const response = await api.get(`/calculations/equity?investmentAmount=${investmentAmount}&postMoneyValuation=${postMoneyValuation}`);
		return response.data;
	},

	// Get rounds by investor (participations)
	getInvestorRounds: async (investorId) => {
		const response = await api.get(`/investment-rounds/investor/${investorId}`);
		return response.data;
	},

	// Get rounds by network (commitments)
	getNetworkRounds: async (networkId) => {
		const response = await api.get(`/investment-rounds/network/${networkId}`);
		return response.data;
	}
};

// Audit Log API
export const auditLogAPI = {
	getAuditLogs: async (page = 0, size = 20, sort = 'timestamp,desc', entityType = null, entityId = null) => {
		let url = `/audit-logs?page=${page}&size=${size}&sort=${sort}`;
		if (entityType) {
			url += `&entityType=${entityType}`;
		}
		if (entityId) {
			url += `&entityId=${entityId}`;
		}
		const response = await api.get(url);
		return response.data;
	}
};

// Network User API
export const networkUserAPI = {
	getNetworkUsers: async (networkId) => {
		const response = await api.get(`/admin/investor-networks/${networkId}/users`);
		return response.data;
	},

	addNetworkUser: async (networkId, userData) => {
		const response = await api.post(`/admin/investor-networks/${networkId}/users`, userData);
		return response.data;
	},

	updateNetworkUser: async (networkId, userId, userData) => {
		const response = await api.put(`/admin/investor-networks/${networkId}/users/${userId}`, userData);
		return response.data;
	},

	removeNetworkUser: async (networkId, userId) => {
		const response = await api.delete(`/admin/investor-networks/${networkId}/users/${userId}`);
		return response.data;
	},

	/** Update network user via my-network endpoint (for MANAGEMENT role; no networkId needed). */
	updateMyNetworkUser: async (userId, userData) => {
		const response = await api.put(`/users/my-network/users/${userId}`, userData);
		return response.data;
	},

	/** Create management user via my-network (MANAGEMENT staff cannot POST /admin/...). */
	addMyNetworkUser: async (userData) => {
		const response = await api.post('/users/my-network/users', userData);
		return response.data;
	},

	getDefaultPermissions: async (networkId, role) => {
		const response = await api.get(`/admin/investor-networks/${networkId}/users/default-permissions/${role}`);
		return response.data;
	}
};

export const startupInvestmentApplicationAPI = {
	getInbox: async (status) => {
		const response = await api.get('/startup-investment-applications/inbox', {
			params: status ? { status } : {}
		});
		return response.data;
	},
	getInboxForTarget: async (targetUserId, targetType, status) => {
		const response = await api.get('/startup-investment-applications/inbox', {
			params: {
				targetUserId,
				targetType,
				...(status ? { status } : {})
			}
		});
		return response.data;
	},
	reviewApplication: async (applicationId, status, reviewNote) => {
		const response = await api.patch(`/startup-investment-applications/${applicationId}/review`, {
			status,
			reviewNote
		});
		return response.data;
	}
};



export default api;





